import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ClipboardList, Loader2, Check, MessageSquareText, Eye } from 'lucide-react';
import type { BuildPlan } from '../../types';
import { GraphPane } from '../GraphPane';

interface PlanReviewProps {
  buildPlan: BuildPlan;
  onApprove: () => void;
  onRequestChanges: (feedback: string) => void;
  onToggleNodeUI?: (nodeId: string, showUI: boolean) => void;
  isProcessing: boolean;
}

export function PlanReview({
  buildPlan,
  onApprove,
  onRequestChanges,
  onToggleNodeUI,
  isProcessing,
}: PlanReviewProps) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');

  const handleRequestChanges = useCallback(() => {
    const trimmed = feedback.trim();
    if (!trimmed) return;
    onRequestChanges(trimmed);
    setFeedback('');
    setShowFeedback(false);
  }, [feedback, onRequestChanges]);

  return (
    <div className="flex-1 flex flex-col p-6 overflow-y-auto">
      <div className="max-w-lg mx-auto w-full space-y-6 flex-1 flex flex-col">
        {/* Header */}
        <div className="text-center space-y-2">
          <ClipboardList className="h-8 w-8 mx-auto text-primary" />
          <h2 className="text-xl font-semibold">Review your build plan</h2>
          <p className="text-sm text-muted-foreground">
            Check the steps below and approve when ready, or request changes.
          </p>
        </div>

        {/* Plan name and description */}
        <div className="space-y-1">
          <h3 className="text-base font-semibold">{buildPlan.name}</h3>
          <p className="text-sm text-muted-foreground">{buildPlan.description}</p>
          <p className="text-xs text-muted-foreground">
            {buildPlan.steps.length} {buildPlan.steps.length === 1 ? 'step' : 'steps'}
          </p>
        </div>

        {/* Mini graph visualization (when graph exists) */}
        {buildPlan.graph && buildPlan.graph.nodes.length > 1 && (
          <div className="rounded-md border overflow-hidden">
            <GraphPane
              processGraph={buildPlan.graph}
              currentNodeId={buildPlan.currentNodeId}
              compact
            />
          </div>
        )}

        {/* Steps list */}
        <div className="space-y-2 flex-1">
          {buildPlan.steps.map((step, index) => {
            const graphNode = buildPlan.graph?.nodes.find((n) => n.id === step.id);
            const isUserInput = graphNode?.type === 'user-input';
            const canToggleUI = graphNode && !isUserInput && onToggleNodeUI;
            return (
              <div key={step.id} className="rounded-md border bg-background p-3 space-y-1">
                <p className="text-sm font-medium">
                  {index + 1}. {step.title}
                </p>
                {step.components.length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    Components: {step.components.join(', ')}
                  </p>
                )}
                {canToggleUI && (
                  <div className="flex items-center gap-2 pt-1">
                    <Checkbox
                      id={`show-ui-${step.id}`}
                      checked={graphNode.showUI ?? false}
                      onCheckedChange={(checked) => onToggleNodeUI(step.id, checked === true)}
                      disabled={isProcessing}
                    />
                    <Label
                      htmlFor={`show-ui-${step.id}`}
                      className="text-xs text-muted-foreground cursor-pointer flex items-center gap-1"
                    >
                      <Eye className="h-3 w-3" />
                      Show a UI for this node
                    </Label>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Feedback textarea (shown when Request Changes is clicked) */}
        {showFeedback && (
          <div className="space-y-2">
            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Describe what you'd like changed..."
              className="min-h-[80px] resize-none"
              disabled={isProcessing}
              autoFocus
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowFeedback(false);
                  setFeedback('');
                }}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleRequestChanges}
                disabled={isProcessing || !feedback.trim()}
              >
                {isProcessing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <MessageSquareText className="mr-2 h-4 w-4" />
                )}
                Send Feedback
              </Button>
            </div>
          </div>
        )}

        {/* Action buttons */}
        {!showFeedback && (
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => setShowFeedback(true)}
              disabled={isProcessing}
              className="flex-1"
              size="lg"
            >
              Request Changes
            </Button>
            <Button onClick={onApprove} disabled={isProcessing} className="flex-1" size="lg">
              {isProcessing ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Check className="mr-2 h-4 w-4" />
              )}
              Approve & Continue
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
